import { legacyInitialState } from "providers/GlobalStateProvider";
import { SettingsManager } from "contexts/SettingsManager/SettingsManager";
import type { SettingsStoreInterface } from "contexts/SettingsManager/SettingsManager";
import { Provider } from "react-redux";
import { Auth0Manager } from "contexts/AuthManager/Auth";
import { ONE_HOUR_OF_MS, ONE_MINUTE_OF_MS } from "./timeUtils";
import type {
  ProductionSegmentType,
  ProcessGroup,
  Process,
  DataDumpAggregateType,
  PnlLosses,
  ManyProductionSegmentsArrays,
  ManyRollupsWithTagsAndRange,
  ManyRollupsWithTagsArrays,
  DataDumpRow,
  RecursivePartial,
  WorkScheduleConfigJsonType,
  CustomerJsonType,
} from "types/types";
import {
  TrendChartBinSizesEnum,
  PageNameEnum,
  PnlCategoriesEnum,
  PnlCategoriesEnumWithOptionalCategories,
} from "types/enums";
import { StoreOverridesType } from "StoreOverridesType";
import { configureStore } from "@reduxjs/toolkit";
import { getStoreMiddleware, rootReducer, RootState } from "store/store";
import { customerInitialState } from "store/customerSlice";
import { objectFromEntries } from "@raven/shared/utils";
import Popper, { PopperProps } from "@mui/material/Popper";
import { useIsStorybook } from "utils/customHooks/storyHooks";
import { Backdrop } from "@mui/material";
import { useMount, Z_INDEX } from "constants/constants";
import { retrotagInitialState } from "store/retrotagSlice";
import { addRollupValues } from "./metrics";
import { action } from "@storybook/addon-actions";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import useFlagsWithDefaults, {
  editableFeatureFlagsAtom,
  FeatureFlags,
} from "./customHooks/useFlagsWithDefaults";
import { useAtom } from "jotai";
import { useRef } from "react";
import { User } from "@auth0/auth0-react";
import {
  FlightConfigType,
  useFlightConfig,
} from "components/DataGetter/FlightConfig/useFlightConfig";
import { merge } from "lodash";
import MachemetryProvider from "providers/MachemetryProvider";

export const STORY_SHIFT_START = new Date("April 5 2023 12:00").setHours(
  0,
  0,
  0,
  0
);
// need to set the time this way to deal with shippable timezone issues

export const oneRollupMetricExample = {
  range: {
    ge: STORY_SHIFT_START,
    lt: STORY_SHIFT_START + 8 * ONE_HOUR_OF_MS,
  },
  tags: {
    notScheduled: {},
    planned: {
      "PC Clean": 20196642,
      Accountability: 8814444,
      Challenges: 142442,
    },
    producing: {},
    uncollected: {},
    unknown: {},
    unplanned: {
      "000000000 LENGTH TEST 00000000(34)": 486645,
    },
    waiting: {},
  },
  counts: {
    producing: {},
    planned: {},
    unplanned: {},
    waiting: {},
    unknown: {},
    notScheduled: {},
    uncollected: {},
  },
  shift: null,
  notScheduled: 0,
  planned: 29153528,
  producing: 4362297,
  totalDurationMs: 35153518,
  uncollected: 0,
  unknown: 1151048,
  unplanned: 486645,
  waiting: 0,
};

export const anotherRollupMetricExample = {
  ...oneRollupMetricExample,
  tags: {
    notScheduled: {},
    planned: {},
    producing: {},
    uncollected: {},
    unknown: {},
    unplanned: {
      "oh no oh no oh no oh no": 486645,
    },
    waiting: {
      WaitingX: 20196642,
      WaitingY: 8814444,
      WaitingZ: 142442,
    },
  },
  notScheduled: 4362297,
  planned: 0,
  producing: 0,
  totalDurationMs: 35153518,
  uncollected: 1151048,
  unknown: 0,
  unplanned: 486645,
  waiting: 29153528,
};

export const process1: Process = {
  id: "test_d_001",
  name: "A2",
  ckey: "test",
  isSelected: true,
  showCountNotMetricOnGroupTile: false,
  callStart: 1579999630.739,
  workScheduleConfig: {
    id: "1",
  },
  taps: [
    {
      id: "test_d_001",
      machemetryId: "test_d_001",
      name: "A2",
    },
  ],
};
const process2: Process = {
  ...process1,
  id: "test_d_002",
  name: "B5C4",
};
const process3: Process = {
  ...process1,
  id: "test_d_003",
  name: "B7",
};

export const oneProcessExample = process1;

export const oneProcessGroupExample: ProcessGroup = {
  groupSortIdx: "0",
  summaryGroup: "all",
  processes: [process1, process2, process3],
  longName: "All Rooms",
  shortName: "All Rooms",
  callStart: null,
  output: null,
  outputLabel: "parts",
  isIdleUntagged: false,
  ckey: "test",
  count: 6907,
  showCountNotMetricOnGroupTile: false,
};

export const oneShiftExample = {
  shift: { name: "example shift" },
  range: { ge: STORY_SHIFT_START - 8 * ONE_HOUR_OF_MS, lt: STORY_SHIFT_START },
};

const now = new Date().getTime();
export const nowFocusRange = {
  ge: now - 4 * ONE_HOUR_OF_MS,
  lt: now + 4 * ONE_HOUR_OF_MS,
};

export const mockDaysWithShifts = [
  [...new Array(3)].map((_, idx) => ({
    ...oneShiftExample,
    range: {
      ge: oneShiftExample.range.ge - idx * 8 * ONE_HOUR_OF_MS,
      lt: oneShiftExample.range.lt - idx * 8 * ONE_HOUR_OF_MS,
    },
  })),
  [...new Array(3)].map((_, idx) => ({
    ...oneShiftExample,
    range: {
      ge: oneShiftExample.range.ge - (idx + 3) * 8 * ONE_HOUR_OF_MS,
      lt: oneShiftExample.range.lt - (idx + 3) * 8 * ONE_HOUR_OF_MS,
    },
  })),
  [...new Array(3)].map((_, idx) => ({
    ...oneShiftExample,
    range: {
      ge: oneShiftExample.range.ge - (idx + 6) * 8 * ONE_HOUR_OF_MS,
      lt: oneShiftExample.range.lt - (idx + 6) * 8 * ONE_HOUR_OF_MS,
    },
  })),
];

export const mockTodaysShifts = mockDaysWithShifts[2];

const MOCK_LOT_INFO = {
  currentLotRowID: "string123",
  lotStartDateMs: 0,
  lotNumber: "lot123",
  nextLotNumber: "nextLot123",
  lotSize: 2000,
  nextLotSize: 3000,
};

export const initialStoryStoreState: typeof legacyInitialState = {
  ...legacyInitialState,
  isAnimationEnabled: false,
  isStorybook: true,
  visibleGroups: [oneProcessGroupExample],
  allGroups: [oneProcessGroupExample],
  rollupsMetricByProcess: {
    [process1.id]: oneRollupMetricExample,
    [process2.id]: oneRollupMetricExample,
    [process3.id]: oneRollupMetricExample,
  },
  todaysShifts: mockTodaysShifts,
  daysWithShifts: mockDaysWithShifts,
  // mock data dump aggregate
  testData: {
    processIds: [process1.id, process2.id, process3.id],
    oeePercent: 73,
    netOutput: 1943,
    effectiveUptimePercent: 73,
    effectiveProducingTimeBottomUp: 73,
    plannedTime: 73,
    productionTime: 73,
    uptimePercent: 73,
    availabilityPercent: 73,
    teepPercent: 73,
  },
  allLotInfo: {
    [process1.id]: MOCK_LOT_INFO,
  },
};
export const StoreAndSettingsContextProvider = ({
  children,
  storeOverrides = {},
  settingsOverrides = {},
  skipAuth = true,
  startWithEmptyStore = false,
  flagsOverrides = {},
  flightConfigOverrides = {},
  mockUser = null,
}: {
  children: any;
  storeOverrides?: StoreOverridesType;
  settingsOverrides?: Partial<SettingsStoreInterface>;
  skipAuth?: boolean;
  startWithEmptyStore?: boolean;
  flagsOverrides?: Partial<FeatureFlags>;
  flightConfigOverrides?: RecursivePartial<FlightConfigType>;
  mockUser?: null | User;
}) => {
  // if we're in storybook, add the body.is-storybook class
  useMount(() => {
    document.body.classList.add("is-storybook");
  });

  const store = configureStore({
    reducer: rootReducer,
    middleware: getStoreMiddleware,
    preloadedState: {
      legacy: {
        ...(startWithEmptyStore ? legacyInitialState : initialStoryStoreState),
        ...storeOverrides?.legacy,
      },
      customer: {
        ...customerInitialState,
        ...storeOverrides?.customer,
      },
      retrotag: {
        ...retrotagInitialState,
        ...storeOverrides?.retrotag,
      },
    },
  });

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns as any}>
      <Provider store={store}>
        <MockFlightConfigProvider flightConfigOverrides={flightConfigOverrides}>
          <Auth0Manager skipAuth={skipAuth} mockUser={mockUser}>
            <SettingsManager settingsOverrides={settingsOverrides}>
              <MachemetryProvider>
                <MockFlags flagsOverrides={flagsOverrides} />
                {children}
              </MachemetryProvider>
            </SettingsManager>
          </Auth0Manager>
        </MockFlightConfigProvider>
      </Provider>
    </LocalizationProvider>
  );
};

function MockFlightConfigProvider({
  children,
  flightConfigOverrides,
}: {
  children: any;
  flightConfigOverrides: RecursivePartial<FlightConfigType>;
}) {
  const [flightConfig, setFlightConfig] = useFlightConfig();
  useMount(() => {
    if (Object.keys(flightConfigOverrides).length > 0) {
      setFlightConfig(merge(flightConfig, flightConfigOverrides));
    }
  });
  return children;
}

function MockFlags({ flagsOverrides }) {
  const flags = useFlagsWithDefaults();
  const [, setEditableFlags] = useAtom(editableFeatureFlagsAtom);
  const initialFlags = useRef(flags);
  useMount(() => {
    // start out with the defaults
    let newFlags = flags;

    // perform any overrides
    Object.entries(flagsOverrides).forEach(([flagName, flagValue]) => {
      newFlags = {
        ...newFlags,
        [flagName]: flagValue,
      };
    });

    setEditableFlags(newFlags);

    return () => {
      // reset to default when we change stories
      setEditableFlags(initialFlags.current);
    };
  });
  return null;
}

export const shortTagShiftRange = {
  shift: { name: "example shift short tag" },
  range: {
    ge: STORY_SHIFT_START - 16 * ONE_HOUR_OF_MS,
    lt: STORY_SHIFT_START - 8 * ONE_HOUR_OF_MS,
  },
};
export const longTagShiftRange = {
  shift: { name: "example shift long tag" },
  range: {
    ge: STORY_SHIFT_START - 24 * ONE_HOUR_OF_MS,
    lt: STORY_SHIFT_START - 16 * ONE_HOUR_OF_MS,
  },
};

export const oneSegmentExample: ProductionSegmentType = {
  pnl: { category: "producing" },
  range: oneShiftExample.range,
};
const anotherSegmentExample: ProductionSegmentType = {
  ...oneSegmentExample,
  pnl: { category: "waiting" },
};
export const shortTagSegmentExample: ProductionSegmentType = {
  tag: { name: "just a normal tag" },
  pnl: { category: "waiting" },
  range: shortTagShiftRange.range,
};
export const longTagSegmentExample: ProductionSegmentType = {
  tag: {
    name: "This is an incredibly long tag, this is to test how long we can make our tag",
  },
  pnl: { category: "notScheduled" },
  range: longTagShiftRange.range,
};

const EMPTY_PNL_LOSS = {
  downtimePlusDependentSpeedLossMs: 0,
  downtimeLossMs: 0,
  downtimeDependentSpeedLossMs: 0,
  tags: [],
  outputLoss: 0,
  numberOfEvents: 0,
  averageDurationMs: 0,
};

export const mockPnlLosses: PnlLosses = {
  range: {
    ge: STORY_SHIFT_START,
    lt: STORY_SHIFT_START + 8 * ONE_HOUR_OF_MS,
  },
  producing: {
    ...EMPTY_PNL_LOSS,
    category: "producing",
    minorStops: { numberOfEvents: 0, averageDurationMs: 0 },
  },
  planned: {
    ...EMPTY_PNL_LOSS,
    category: "planned",
    downtimePlusDependentSpeedLossMs: 1000,
    downtimeLossMs: 1000,
    downtimeDependentSpeedLossMs: 1000,
    numberOfEvents: 1,
    averageDurationMs: 1000,
  },
  waiting: {
    ...EMPTY_PNL_LOSS,
    category: "waiting",
    downtimePlusDependentSpeedLossMs: 1000,
    downtimeLossMs: 1000,
    downtimeDependentSpeedLossMs: 1000,
    numberOfEvents: 1,
    averageDurationMs: 1000,
  },
  unplanned: {
    ...EMPTY_PNL_LOSS,
    category: "unplanned",
    downtimePlusDependentSpeedLossMs: 1000,
    downtimeLossMs: 1000,
    downtimeDependentSpeedLossMs: 1000,
    numberOfEvents: 1,
    averageDurationMs: 1000,
  },
  notScheduled: {
    ...EMPTY_PNL_LOSS,
    category: "notScheduled",
    downtimePlusDependentSpeedLossMs: 1000,
    downtimeLossMs: 1000,
    downtimeDependentSpeedLossMs: 1000,
    numberOfEvents: 1,
    averageDurationMs: 1000,
  },
  unknown: {
    ...EMPTY_PNL_LOSS,
    category: "unknown",
    downtimePlusDependentSpeedLossMs: 1000,
    downtimeLossMs: 1000,
    downtimeDependentSpeedLossMs: 1000,
    downtimeDependentSlowdownLossMs: 1000,
    downtimeDependentMinorStopLossMs: 1000,
    independentSpeedLossMs: 1000,
    independentSlowdownLossMs: 1000,
    independentMinorStopLossMs: 1000,
    speedLossMs: 1000,
    slowdownLossMs: 1000,
    minorStopLossMs: 1000,
    allocatableTimeMs: 1000,
    numberOfEvents: 1,
    averageDurationMs: 1000,
  },
  quality: {
    category: "quality",
    qualityLossMs: 1000,
    qualityLossOutput: 1000,
    scrapLossMs: 1000,
    scrapLossOutput: 1000,
    reworkLossMs: 1000,
    reworkLossOutput: 1000,
  },
  uncollected: {
    category: "uncollected",
    totalDurationMs: 1000,
    numberOfEvents: 1,
    averageDurationMs: 1000,
  },
  totalAllocatedTimeMs: 0,
};

export const mockEmptyPnlLosses: PnlLosses = {
  range: {
    ge: 1585906200000,
    lt: 1585935000000,
  },
  totalAllocatedTimeMs: 0,
  producing: {
    category: "producing",
    minorStops: { numberOfEvents: 0, averageDurationMs: 0 },
    numberOfEvents: 0,
    averageDurationMs: 0,
    outputLoss: 0,
  },
  planned: {
    category: "planned",
    downtimePlusDependentSpeedLossMs: 0,
    downtimeLossMs: 0,
    downtimeDependentSpeedLossMs: 0,
    tags: [] as any,
    numberOfEvents: 0,
    averageDurationMs: 0,
    outputLoss: 0,
  },
  waiting: {
    category: "waiting",
    downtimePlusDependentSpeedLossMs: 0,
    downtimeLossMs: 0,
    downtimeDependentSpeedLossMs: 0,
    tags: [] as any,
    numberOfEvents: 0,
    averageDurationMs: 0,
    outputLoss: 0,
  },
  unplanned: {
    category: "unplanned",
    downtimePlusDependentSpeedLossMs: 0,
    downtimeLossMs: 0,
    downtimeDependentSpeedLossMs: 0,
    tags: [] as any,
    numberOfEvents: 0,
    averageDurationMs: 0,
    outputLoss: 0,
  },
  notScheduled: {
    category: "not_scheduled",
    downtimePlusDependentSpeedLossMs: 0,
    downtimeLossMs: 0,
    downtimeDependentSpeedLossMs: 0,
    tags: [] as any,
    numberOfEvents: 0,
    averageDurationMs: 0,
    outputLoss: 0,
  },
  unknown: {
    category: "unknown",
    downtimePlusDependentSpeedLossMs: 0,
    downtimeLossMs: 0,
    downtimeDependentSpeedLossMs: 0,
    downtimeDependentSlowdownLossMs: 0,
    downtimeDependentMinorStopLossMs: 0,
    independentSpeedLossMs: 0,
    independentSlowdownLossMs: 0,
    independentMinorStopLossMs: 0,
    speedLossMs: 0,
    slowdownLossMs: 0,
    minorStopLossMs: 0,
    allocatableTimeMs: 0,
    numberOfEvents: 0,
    averageDurationMs: 0,
    outputLoss: 0,
  },
  quality: {
    category: "quality",
    qualityLossMs: 0,
    qualityLossOutput: 0,
    scrapLossMs: 0,
    scrapLossOutput: 0,
    reworkLossMs: 0,
    reworkLossOutput: 0,
  },
  uncollected: {
    category: "uncollected",
    totalDurationMs: 0,
    numberOfEvents: 0,
    averageDurationMs: 0,
  },
};

export const mockPnlLossesRealistic: PnlLosses = {
  range: {
    ge: 1621359000000,
    lt: 1621391400000,
  },
  totalAllocatedTimeMs: 780000,
  producing: {
    category: "producing",
    outputLoss: -3038.308333333333,
    numberOfEvents: 88,
    averageDurationMs: 45117.73863636364,
    minorStops: {
      numberOfEvents: 71,
      averageDurationMs: 9192.070422535211,
    },
  },
  planned: {
    category: "planned",
    outputLoss: -53704.15833333333,
    numberOfEvents: 1,
    averageDurationMs: 6444499,
    downtimePlusDependentSpeedLossMs: 6444499,
    downtimeLossMs: 6444499,
    downtimeDependentSpeedLossMs: 0,
    tags: [
      {
        name: "135min Product Change",
        numberOfEvents: 1,
        averageDurationMs: 6444499,
        path: ["Idle", "Changeover", "135minProductChange"],
        downtimePlusDependentSpeedLossMs: 6444499,
        downtimeLossMs: 6444499,
        downtimeDependentSpeedLossMs: 0,
        changeoverTime: null,
        outputLoss: -53704.15833333333,
      },
    ],
  },
  waiting: {
    category: "waiting",
    outputLoss: -2181,
    numberOfEvents: 14,
    averageDurationMs: 74408.57142857143,
    downtimePlusDependentSpeedLossMs: 1115156,
    downtimeLossMs: 1041720,
    downtimeDependentSpeedLossMs: 73436,
    tags: [
      {
        name: "PVC Foil Change - Thermoformer",
        numberOfEvents: 11,
        averageDurationMs: 23792.727272727272,
        path: ["Idle", "Thermoformer", "PVCFoilChangeThermoformer"],
        downtimePlusDependentSpeedLossMs: 335156,
        downtimeLossMs: 261720,
        downtimeDependentSpeedLossMs: 73436,
        changeoverTime: null,
        outputLoss: -2792.9666666666667,
      },
      {
        name: "Challenges - A1",
        numberOfEvents: 1,
        averageDurationMs: 240000,
        path: [],
        downtimePlusDependentSpeedLossMs: 240000,
        downtimeLossMs: 240000,
        downtimeDependentSpeedLossMs: 0,
        changeoverTime: null,
        outputLoss: 0,
      },
      {
        name: "Challenges - A2",
        numberOfEvents: 1,
        averageDurationMs: 180000,
        path: [],
        downtimePlusDependentSpeedLossMs: 180000,
        downtimeLossMs: 180000,
        downtimeDependentSpeedLossMs: 0,
        changeoverTime: null,
        outputLoss: 0,
      },
      {
        name: "Challenges - Thermoformer",
        numberOfEvents: 1,
        averageDurationMs: 360000,
        path: [],
        downtimePlusDependentSpeedLossMs: 360000,
        downtimeLossMs: 360000,
        downtimeDependentSpeedLossMs: 0,
        changeoverTime: null,
        outputLoss: 0,
      },
    ],
  },
  unplanned: {
    category: "unplanned",
    outputLoss: -12122.741666666669,
    numberOfEvents: 14,
    averageDurationMs: 103909.21428571429,
    downtimePlusDependentSpeedLossMs: 1556124,
    downtimeLossMs: 1454729,
    downtimeDependentSpeedLossMs: 101395,
    tags: [
      {
        name: "23 - VSM1 overhead conveyor transition to spiral to Dividella",
        numberOfEvents: 5,
        averageDurationMs: 245434,
        path: [
          "Idle",
          "Transitions",
          "FeederA1",
          "23VSM1OverheadConveyorTransitionToSpiralToDividella",
        ],
        downtimePlusDependentSpeedLossMs: 1256188,
        downtimeLossMs: 1227170,
        downtimeDependentSpeedLossMs: 29018,
        changeoverTime: null,
        outputLoss: -10468.233333333334,
      },
      {
        name: "Shipper Jam - CPP",
        numberOfEvents: 2,
        averageDurationMs: 31227.5,
        path: ["Idle", "CasePackerPalletizer", "ShipperJamCPP"],
        downtimePlusDependentSpeedLossMs: 74450,
        downtimeLossMs: 62455,
        downtimeDependentSpeedLossMs: 11995,
        changeoverTime: null,
        outputLoss: -620.4166666666667,
      },
      {
        name: "Remove Product from Feeder",
        numberOfEvents: 3,
        averageDurationMs: 35484,
        path: ["Idle", "Feeder", "RemoveProductFromFeeder"],
        downtimePlusDependentSpeedLossMs: 134820,
        downtimeLossMs: 106452,
        downtimeDependentSpeedLossMs: 28368,
        changeoverTime: null,
        outputLoss: -1123.5,
      },
      {
        name: "Stacker Jam - Cartoner",
        numberOfEvents: 4,
        averageDurationMs: 14663,
        path: ["Idle", "Cartoner", "StackerJamCartoner"],
        downtimePlusDependentSpeedLossMs: 90666,
        downtimeLossMs: 58652,
        downtimeDependentSpeedLossMs: 32014,
        changeoverTime: null,
        outputLoss: -755.55,
      },
    ],
  },
  notScheduled: {
    category: "notScheduled",
    outputLoss: -32563.566666666666,
    numberOfEvents: 13,
    averageDurationMs: 300586.76923076925,
    downtimePlusDependentSpeedLossMs: 4010171,
    downtimeLossMs: 3907628,
    downtimeDependentSpeedLossMs: 102543,
    tags: [
      {
        name: "Engineering Test",
        numberOfEvents: 13,
        averageDurationMs: 300586.76923076925,
        path: ["Idle", "EngineeringTest"],
        downtimePlusDependentSpeedLossMs: 4010171,
        downtimeLossMs: 3907628,
        downtimeDependentSpeedLossMs: 102543,
        outputLoss: -33418.09166666667,
      },
    ],
  },
  unknown: {
    category: "unknown",
    outputLoss: 0,
    numberOfEvents: 4,
    averageDurationMs: 79374,
    downtimePlusDependentSpeedLossMs: 340835,
    downtimeLossMs: 317496,
    downtimeDependentSpeedLossMs: 23339,
    downtimeDependentSlowdownLossMs: 0,
    downtimeDependentMinorStopLossMs: 23339,
    independentSpeedLossMs: 272813,
    independentSlowdownLossMs: 130917,
    independentMinorStopLossMs: 141896,
    speedLossMs: 296152,
    slowdownLossMs: 130917,
    minorStopLossMs: 165235,
    allocatableTimeMs: 613648,
  },
  quality: {
    category: "quality",
    qualityLossMs: 97200,
    qualityLossOutput: 810,
    scrapLossMs: 0,
    scrapLossOutput: 0,
    reworkLossMs: 69600,
    reworkLossOutput: 810,
  },
  uncollected: {
    category: "uncollected",
    totalDurationMs: 0,
    numberOfEvents: 0,
    averageDurationMs: 0,
  },
};
export const mockDataDumpRow: DataDumpRow = {
  ge: String(oneShiftExample.range.ge),
  lt: String(oneShiftExample.range.ge + ONE_HOUR_OF_MS),
  operatorId: "team.a",
  targetRate: 480,
  actualRate: 360,
  rateDenominator: 60000,
  skuCode: "sku_code_123",
  skuName: "Sku Name 123",
  callStart: null,
  callTarget: null,
  skuStart: 1628600000000,
  messageTime: 1628600000000,
  output: 600,
  netOutput: 600,
  grossOutput: 600,
  rejectOutput: 30,
  teamSize: 3,
  skuScale: 1,
  machemetryId: "example_d_001",
  pnlCategory: PnlCategoriesEnumWithOptionalCategories.planned,
};

export const mockDataDump: DataDumpAggregateType = {
  processIds: [oneProcessExample.id],
  processNames: [oneProcessExample.name],
  range: {
    ge: oneShiftExample.range.ge,
    lt: oneShiftExample.range.ge + ONE_HOUR_OF_MS,
  },
  netOutput: 70,
  netTicks: 70,
  grossTicks: 100,
  grossOutput: 100,
  rejectTicks: 10,
  rejectOutput: 10,
  outputLossSum: -10,
  productionPotentialOutput: 80,
  totalPotentialOutput: 100,
  operationalAvailabilityPercent: 20,
  efficiencyPercent: 20,
  uslePercent: 40,
  oeePercent: 50,
  availabilityPercent: 40,
  performancePercent: 45,
  uptimePercent: 20,
  teepPercent: 30,
  teepPercentBottomUp: 30,
  effectiveUptimePercent: 50,
  productionTime: 60 * 60000,
  producingTime: 0,
  effectiveProducingTime: 1000,
  effectiveProducingTimeTopDown: 1000,
  speedLossTime: 1000,
  slowdownLossTime: 1000,
  slowdownLossTimeReal: 1000,
  minorStopTime: 1000,
  rejectLossTime: 1000,
  plannedTime: 1000,
  waitingTime: 1000,
  unplannedTime: 1000,
  unknownTime: 1000,
  notScheduledTime: 1000,
  uncollectedTime: 1000,
  unplannedPercent: 50,
  qualityPercent: 0,
  taggingPercent: 0,
  meanTimeBetweenFailures: 0,
  meanTimeToRecovery: 0,
  avgCycleTime: 0,
  ticksPort1: 0,
  ticksPort2: 0,
  ticksPort3: 0,
  ticksPort4: 0,
  customMetric01Value: null,
  customMetric02Value: null,
  customMetric03Value: null,
  customMetric04Value: null,
  customMetric05Value: null,
  customMetric06Value: null,
  customMetric07Value: null,
  customMetric08Value: null,
  untaggedPercent: null,
  notScheduledPercent: null,
  plannedDowntimeEfficiency: 85.2,
  effectiveProducingTimeBottomUp: 0,
  pureEquipmentUptimePercent: 20,
  uptimePerformancePercent: 20,
  producingEfficiencyPercent: 20,
  pnlLosses: mockPnlLosses,
  lastSegment: mockDataDumpRow,
};

export const mockDataDumpRealistic = {
  ...mockDataDump,
  pnlLosses: mockPnlLossesRealistic,
};

const anotherMockDataDump: DataDumpAggregateType = {
  ...mockDataDump,
  oeePercent: 0,
  teepPercent: 10,
  availabilityPercent: 20,
  uptimePercent: 70,
};

const PROCESS_ID = oneProcessExample.id;

export const getTimelineTooltipStore = () => ({
  segments: {
    [PROCESS_ID]: [
      oneSegmentExample,
      shortTagSegmentExample,
      longTagSegmentExample,
    ],
  },
});

export const getMockGridPageStore = (
  ckey: string,
  mockLastDataDumpRow?: DataDumpRow
): RootState => {
  const mockDataDumps = [...new Array(8)].map((_, idx) => ({
    ...mockDataDump,
    pnlLosses: {
      ...mockDataDump.pnlLosses,
      unplanned: {
        ...mockDataDump.pnlLosses.unplanned,
        downtimePlusDependentSpeedLossMs: ONE_HOUR_OF_MS,
      },
    },
    processIds: [PROCESS_ID],
    range: {
      ge: oneShiftExample.range.ge + (idx - 1) * ONE_HOUR_OF_MS,
      lt: oneShiftExample.range.ge + idx * ONE_HOUR_OF_MS,
    },
    lastSegment: mockLastDataDumpRow ?? mockDataDump.lastSegment,
  }));
  const mockDataDumpsGroupTiles = {
    ...objectFromEntries([
      ["all", mockDataDump],
      ...(mockDataDumps.map((dump, idx) => [`${idx}`, dump]) as [
        string,
        DataDumpAggregateType,
      ][]),
    ]),
  } as { [processId: string]: DataDumpAggregateType };
  return {
    legacy: {
      ...legacyInitialState,
      visibleProcesses: [oneProcessExample],
      allProcessesRollupMetric: addRollupValues(
        [...Array(6)].map(_ => oneRollupMetricExample)
      ),
      isStorybook: true,
      allRollupsMetricTrendChartBarsArrays: {
        [PROCESS_ID]: [...new Array(8)].map((_, idx) => ({
          ...oneRollupMetricExample,
          planned: 0.4 * ONE_HOUR_OF_MS,
          unplanned: 0.3 * ONE_HOUR_OF_MS,
          unknown: 0.2 * ONE_HOUR_OF_MS,
          producing: 0.1 * ONE_HOUR_OF_MS,
          range: mockDataDumps[idx].range,
        })),
      },
      segments: {
        [PROCESS_ID]: [
          {
            ...oneSegmentExample,
            pnl: { category: PnlCategoriesEnum.planned },
            range: {
              ge: oneShiftExample.range.ge,
              lt: oneShiftExample.range.ge + 10 * ONE_MINUTE_OF_MS,
            },
          },
          {
            ...oneSegmentExample,
            pnl: { category: PnlCategoriesEnum.unknown },
            range: {
              ge: oneShiftExample.range.ge + 10 * ONE_MINUTE_OF_MS,
              lt: oneShiftExample.range.ge + 20 * ONE_MINUTE_OF_MS,
            },
          },
          {
            ...oneSegmentExample,
            pnl: { category: PnlCategoriesEnum.unplanned },
            range: {
              ge: oneShiftExample.range.ge + 20 * ONE_MINUTE_OF_MS,
              lt: oneShiftExample.range.ge + 30 * ONE_MINUTE_OF_MS,
            },
          },
          {
            ...oneSegmentExample,
            range: {
              ge: oneShiftExample.range.ge + 30 * ONE_MINUTE_OF_MS,
              lt: oneShiftExample.range.lt,
            },
          },
        ],
      },
      dataDumpFullRange: {
        ...mockDataDump,
        processIds: [PROCESS_ID],
        range: oneShiftExample.range,
      },
      dataDumpsTrendChart: mockDataDumps,
      dataDumpsGroupTiles: mockDataDumpsGroupTiles,
      dataDumpHubletView: mockDataDump,
      lastDataDumpRow: mockLastDataDumpRow ?? mockDataDumpRow,
      visibleGroups: [
        {
          ...oneProcessGroupExample,
          processes: [
            { ...oneProcessExample, ckey, name: "Machine A", id: PROCESS_ID },
          ],
        },
      ],
      allGroups: [
        {
          ...oneProcessGroupExample,
          processes: [
            { ...oneProcessExample, ckey, name: "Machine A", id: PROCESS_ID },
          ],
        },
      ],
      rollupsMetricByProcess: {
        [oneProcessGroupExample.processes[0].id]: oneRollupMetricExample,
        [oneProcessGroupExample.processes[1].id]: oneRollupMetricExample,
        [oneProcessGroupExample.processes[2].id]: oneRollupMetricExample,
      },
      trendChartBinSize: TrendChartBinSizesEnum.hours,
      selectedProcessIdsRef: { current: [PROCESS_ID] },
      shifts: [oneShiftExample],
      todaysShifts: mockTodaysShifts,
      daysWithShifts: mockDaysWithShifts,
      selectedShift: oneShiftExample,
      selectedDay: {
        ...oneShiftExample,
        range: {
          ...oneShiftExample.range,
          lt: oneShiftExample.range.lt + 8 * ONE_HOUR_OF_MS,
        },
      },
      focusRange: oneShiftExample.range,
      shouldFetchNewShifts: false,
      allLotInfo: {
        [oneProcessExample.id]: MOCK_LOT_INFO,
      },
    },
    customer: {
      ...customerInitialState,
      ckey,
      summaryGroup: oneProcessGroupExample.summaryGroup,
      detailKey: "0",
    },
    retrotag: retrotagInitialState,
  };
};

export const getMockTimelinePageStore = ({
  isParallelConstraints = false,
} = {}): StoreOverridesType => {
  const getIsSelected = idx => (isParallelConstraints ? idx <= 2 : true);
  const TIMELINE_PAGE_CKEY = isParallelConstraints
    ? "timeline_page_story_parallel_serial"
    : "timeline_page_story";

  const processes: Process[] = [...new Array(6)].map((_, idx) => ({
    ...oneProcessGroupExample.processes[0],
    // first 3 processes are constraints
    isSelected: getIsSelected(idx),
    name: `ABC_${idx}`,
    id: `test_d_00${idx}`,
    ckey: TIMELINE_PAGE_CKEY,
  }));
  const visibleGroups = [
    {
      ...oneProcessGroupExample,
      ckey: TIMELINE_PAGE_CKEY,
      summaryGroup: "abc",
      processes,
    },
  ];
  const mockGridPageStore = getMockGridPageStore(TIMELINE_PAGE_CKEY);

  const rollupsMetricByProcess = objectFromEntries(
    processes.map(({ id }, idx) => [
      id,
      getIsSelected(idx) ? oneRollupMetricExample : anotherRollupMetricExample,
    ])
  ) as ManyRollupsWithTagsAndRange;
  const dataDumpsFullRangeByProcess = objectFromEntries(
    processes.map(({ id }, idx) => [
      id,
      {
        ...(getIsSelected(idx) ? mockDataDump : anotherMockDataDump),
        processIds: [id],
        range: oneShiftExample.range,
      },
    ])
  ) as {
    [processId: string]: DataDumpAggregateType;
  };
  const allRollupsMetricTrendChartBarsArrays = {
    ...objectFromEntries(
      processes.map(({ id }, idx) => [
        id,
        [...new Array(8)].map((__, subIdx) => ({
          ...(getIsSelected(idx)
            ? oneRollupMetricExample
            : anotherRollupMetricExample),
          range: {
            ge: oneShiftExample.range.ge + subIdx * ONE_HOUR_OF_MS,
            lt: oneShiftExample.range.lt + (subIdx + 1) * ONE_HOUR_OF_MS,
          },
        })),
      ])
    ),
  } as ManyRollupsWithTagsArrays;

  const segments = objectFromEntries(
    processes.map(({ id }, idx) => [
      id,
      [getIsSelected(idx) ? oneSegmentExample : anotherSegmentExample],
    ])
  ) as ManyProductionSegmentsArrays;

  const dataDumpFullRange: DataDumpAggregateType = {
    ...mockDataDump,
    processIds: processes
      .filter((_, idx) => getIsSelected(idx))
      .map(({ id }) => id),
  };

  return {
    legacy: {
      ...mockGridPageStore.legacy,
      storyPage: PageNameEnum.timeline,
      segments,
      selectedProcessIdsRef: {
        current: processes
          .map(({ id }) => id)
          .filter((id, idx) => getIsSelected(idx)),
      },
      visibleGroups,
      visibleProcesses: processes,
      allGroups: visibleGroups,
      dataDumpFullRange,
      dataDumpsFullRangeByProcess,
      rollupsMetricByProcess,
      allRollupsMetricTrendChartBarsArrays,
      // TODO
      // latest: {
      //   ...mockTilesPageStore.legacy.latest,
      //   [processes[0].id]: {
      //     ...mockTilesPageStore.legacy.latest[processes[0].id],
      //     callTarget: "maint",
      //   },
      //   [processes[1].id]: {
      //     ...mockTilesPageStore.legacy.latest[processes[1].id],
      //     callTarget: "maint",
      //   },
      // },
    },
    customer: {
      ...mockGridPageStore.customer,
      ckey: TIMELINE_PAGE_CKEY,
      summaryGroup: "abc",
    },
  };
};
export const getMockTimelinePageStoreWithOverlappingSegments = () => {
  const store = getMockTimelinePageStore();
  const TIMELINE_PAGE_CKEY = "timeline_page_story";
  return {
    customer: {
      ckey: TIMELINE_PAGE_CKEY,
      summaryGroup: "abc",
    },
    legacy: {
      ...store?.legacy,
      segments: {
        [PROCESS_ID]: [
          {
            ...oneSegmentExample,
            pnl: { category: PnlCategoriesEnum.planned },
            range: {
              ge: oneShiftExample.range.ge,
              lt: oneShiftExample.range.ge + 10 * ONE_MINUTE_OF_MS,
            },
            ckey: TIMELINE_PAGE_CKEY,
          },
          // this segment overlaps with previous (2 minutes)
          {
            ...oneSegmentExample,
            pnl: { category: PnlCategoriesEnum.unknown },
            range: {
              ge: oneShiftExample.range.ge + 8 * ONE_MINUTE_OF_MS,
              lt: oneShiftExample.range.ge + 22 * ONE_MINUTE_OF_MS,
            },
            ckey: TIMELINE_PAGE_CKEY,
          },
          // this segment overlaps with previous (2 minutes)
          {
            ...oneSegmentExample,
            pnl: { category: PnlCategoriesEnum.unplanned },
            range: {
              ge: oneShiftExample.range.ge + 20 * ONE_MINUTE_OF_MS,
              lt: oneShiftExample.range.ge + 30 * ONE_MINUTE_OF_MS,
            },
            ckey: TIMELINE_PAGE_CKEY,
          },
          {
            ...oneSegmentExample,
            range: {
              ge: oneShiftExample.range.ge + 30 * ONE_MINUTE_OF_MS,
              lt: oneShiftExample.range.lt,
            },
            ckey: TIMELINE_PAGE_CKEY,
          },
        ],
      },
    },
  };
};

export const StoryStoreDecorator = storyFn => (
  <StoreAndSettingsContextProvider>{storyFn()}</StoreAndSettingsContextProvider>
);

export const getMockTilesPageStore = (ckey: string): RootState => {
  const mockStore = getMockGridPageStore(ckey);
  const mockGroup = mockStore.legacy.visibleGroups[0];
  const mockProcess = mockGroup.processes[0];
  const mockProcess1 = { ...mockProcess, id: "test_d_001" };
  const mockProcess2 = { ...mockProcess, id: "test_d_002" };
  const mockProcess3 = { ...mockProcess, id: "test_d_003" };
  const mockGroup1: ProcessGroup = {
    ...mockGroup,
    groupSortIdx: "0",
    summaryGroup: "all",
    shortName: "All Machines",
    processes: [mockProcess1, mockProcess2, mockProcess3],
  };
  const mockGroup2: ProcessGroup = {
    ...mockGroup,
    groupSortIdx: "1",
    summaryGroup: "group-1",
    shortName: "Group 1",
    processes: [mockProcess1, mockProcess2],
  };
  const mockGroup3: ProcessGroup = {
    ...mockGroup,
    groupSortIdx: "1",
    summaryGroup: "group-1",
    shortName: "Group 1",
    processes: [mockProcess3],
  };
  return {
    legacy: {
      ...legacyInitialState,
      isStorybook: true,
      rollupsMetricByProcess: {
        [mockProcess1.id]: {
          ...oneRollupMetricExample,
          producing: 7 * ONE_HOUR_OF_MS,
        },
        [mockProcess2.id]: {
          ...oneRollupMetricExample,
          producing: 1 * ONE_HOUR_OF_MS,
        },
        [mockProcess3.id]: {
          ...oneRollupMetricExample,
          notScheduled: 6 * ONE_HOUR_OF_MS,
        },
      },
      selectedProcessIdsRef: {
        current: [mockProcess1.id, mockProcess2.id, mockProcess3.id],
      },
      dataDumpFullRange: {
        ...mockDataDump,
        processIds: [PROCESS_ID],
        range: oneShiftExample.range,
      },
      dataDumpsGroupTiles: {
        [mockGroup1.summaryGroup]: { ...mockDataDump },
        [mockGroup2.summaryGroup]: { ...mockDataDump },
        [mockGroup3.summaryGroup]: { ...mockDataDump },
      } as { [groupName: string]: DataDumpAggregateType },
      dataDumpsFullRangeByProcess: {
        [mockProcess1.id]: { ...mockDataDump },
        [mockProcess2.id]: { ...mockDataDump },
        [mockProcess3.id]: { ...mockDataDump },
      },
      visibleGroups: [mockGroup1, mockGroup2, mockGroup3],
      allGroups: [mockGroup1, mockGroup2, mockGroup3],
      focusRange: oneRollupMetricExample.range,
      segments: {
        [mockProcess1.id]: [oneSegmentExample],
        [mockProcess2.id]: [oneSegmentExample],
        [mockProcess3.id]: [oneSegmentExample],
      },
    },
    customer: {
      ...customerInitialState,
      ckey,
    },
    retrotag: retrotagInitialState,
  };
};

/** Portal (used by Popper) doesn't cooperate with storyshots.
 * So, in storybook, just use a div.
 */
export function PopperStorybookFriendly({
  children,
  open,
  ...props
}: PopperProps) {
  const isStorybook = useIsStorybook();

  return isStorybook ? (
    open ? (
      <div {...(props as any)}>{children}</div>
    ) : null
  ) : (
    <Backdrop
      className="popperBackdrop"
      open={open}
      style={{
        zIndex: Z_INDEX.L11,
        backgroundColor: "rgba(16,24,46, 0.9)",
        pointerEvents: "none",
        right: "16px", // Leave the rightmost 16px uncovered to keep scrollbar visible
      }}
    >
      <Popper {...{ open, ...props }}>{children}</Popper>
    </Backdrop>
  );
}

export const mockRollupDisplay = {
  range: {
    ge: 1582174800000,
    lt: 1582209953518,
  },
  totalDurationMs: 1582209953518 - 1582174800000,
  producing: 15476465,
  planned: 3991964,
  unplanned: 40605,
  waiting: 1974692,
  unknown: 4904659,
  notScheduled: 16785132,
  uncollected: 0,
  tags: {
    producing: {},
    planned: {
      "Changement de feuille": 2459148,
      "Livraison de produit": 255910,
      Setup: 1276906,
    },
    unplanned: {
      Maintenance: 40605,
    },
    waiting: {
      "Support / Ingénierie": 1974692,
    },
    unknown: {},
    notScheduled: {
      "Arret de production": 15622084,
      Pause: 1163048,
    },
    uncollected: {},
  },
  counts: {
    producing: {},
    planned: {
      "Changement de feuille": 1,
      "Livraison de produit": 1,
      Setup: 1,
    },
    unplanned: {
      Maintenance: 1,
    },
    waiting: {
      "Support / Ingénierie": 1,
    },
    unknown: {},
    notScheduled: {
      "Arret de production": 1,
      Pause: 1,
    },
    uncollected: {},
  },
};

export const exampleAuthContext = {
  user: {
    nickname: "ExampleUserName",
    name: "Example User",
    email: "example.user@email.com",
    picture: "/images/rt.png",
    rid: "1234567890",
  },
  isAuthenticated: true,
  loading: false,
  loginWithRedirect: () => {
    action("sign-in");
    return Promise.resolve();
  },
  logout: action("sign-out"),
  getTokenSilently: () => {
    action("get-token-silently");
    return Promise.resolve();
  },
  getIdTokenClaims: () => {
    action("get-token-claims");
    return undefined;
  },
  getTokenWithPopup: () => {
    action("get-token-with-popup");
    return Promise.resolve("shhhh");
  },
  auth0Client: {} as any,
};

export const MOCK_WORK_SCHEDULE_CONFIG: WorkScheduleConfigJsonType = {
  id: "1",
  name: "Default",
  timeZone: "US/Eastern",
  operational: true,
  startDate: "2018-01-01",
  endDate: null,
  rotationLength: 1,
  processes: [
    {
      taps: [
        {
          name: "Sense Testing 004",
          machemetryId: "raveng_d_004",
        },
      ],
    },
    {
      taps: [
        {
          name: "Sense Testing 003",
          machemetryId: "raveng_d_003",
        },
      ],
    },
    {
      taps: [
        {
          name: "Sense Testing 001",
          machemetryId: "raveng_d_001",
        },
      ],
    },
    {
      taps: [
        {
          name: "Test S8",
          machemetryId: "raveng_d_s8",
        },
      ],
    },
    {
      taps: [
        {
          name: "Test S3",
          machemetryId: "raveng_d_s3",
        },
      ],
    },
    {
      taps: [
        {
          name: "Test S7",
          machemetryId: "raveng_d_s7",
        },
      ],
    },
    {
      taps: [
        {
          name: "Test S4",
          machemetryId: "raveng_d_s4",
        },
      ],
    },
    {
      taps: [
        {
          name: "Test S5",
          machemetryId: "raveng_d_s5",
        },
      ],
    },
    {
      taps: [
        {
          name: "Scooby-Doo",
          machemetryId: "raveng_d_03_7_0",
        },
      ],
    },
    {
      taps: [
        {
          name: "Scrappy-Doo",
          machemetryId: "raveng_d_03_8_0",
        },
      ],
    },
    {
      taps: [
        {
          name: "Test S9",
          machemetryId: "raveng_d_s9",
        },
      ],
    },
    {
      taps: [
        {
          name: "Test S1",
          machemetryId: "raveng_d_s1",
        },
      ],
    },
    {
      taps: [
        {
          name: "TEST WSC Default",
          machemetryId: "raveng_d_014",
        },
      ],
    },
    {
      taps: [
        {
          name: "Garfield",
          machemetryId: "raveng_d_012",
        },
      ],
    },
    {
      taps: [
        {
          name: "140 Labeler",
          machemetryId: "raveng_d_s10",
        },
      ],
    },
    {
      taps: [
        {
          name: "Test S6",
          machemetryId: "raveng_d_s6",
        },
      ],
    },
  ],
  rotation: [
    {
      name: "Day 1 of Default",
      ordinal: 1,
      shiftsCount: 2,
      shifts: [
        {
          id: "1",
          name: "Day",
          startTime: "07:00",
          endTime: "19:00",
        },
        {
          id: "2",
          name: "Midnight",
          startTime: "19:00",
          endTime: "07:00",
        },
      ],
    },
  ],
};
export const MOCK_CUSTOMER_INFO_JSON: CustomerJsonType = {
  timeZone: "US/Eastern",
  rateUnits: "Units/Hour",
  staleTime: 120,
  valueMaps: {
    dataSourceValueMap: {},
  },
  abbrevName: "ravenjest2",
  lossGroups: {
    Quality: {
      name: "Quality",
      lossId: "Quality",
      subLossGroups: [],
    },
    Unknown: {
      name: "Unknown",
      lossId: "Unknown",
      subLossGroups: [],
    },
    PlannedPL: {
      name: "Planned",
      lossId: "PlannedPL",
      subLossGroups: ["Idle/Planned"],
    },
    RunningPL: {
      name: "Running",
      lossId: "RunningPL",
      subLossGroups: [],
    },
    UnknownPL: {
      name: "Unknown",
      lossId: "UnknownPL",
      subLossGroups: ["Idle/Unknown", "Idle/Untagged", "Unknown"],
    },
    WaitingPL: {
      name: "Waiting",
      lossId: "WaitingPL",
      subLossGroups: ["Idle/Waiting"],
    },
    "All Losses": {
      name: "All Losses",
      lossId: "All Losses",
      subLossGroups: [
        "Changeovers",
        "Equipment Downtime",
        "Material and Labour Shortage",
        "Minor Stops",
        "Planned Downtime",
        "Planned Maintenance",
        "Quality",
        "Setup and Adjust",
        "Slow Cycles",
        "Unaccounted for Time",
        "Uncollected",
        "Unscheduled Time",
      ],
    },
    "Idle/Other": {
      date: "2021-06-28",
      name: "Other",
      lossId: "Idle/Other",
      suppressProducingInStickyTag: false,
    },
    AllLossesPL: {
      name: "All Losses PL",
      lossId: "AllLossesPL",
      subLossGroups: [
        "NoProductionScheduledPL",
        "ProducingPL",
        "ProductionLossPL",
        "UncollectedPL",
      ],
    },
    Changeovers: {
      name: "Changeovers",
      lossId: "Changeovers",
      subLossGroups: [],
    },
    "Minor Stops": {
      name: "Minor Stops",
      lossId: "Minor Stops",
      subLossGroups: [],
    },
    PlantLossPL: {
      name: "Plant Loss",
      lossId: "PlantLossPL",
      subLossGroups: [],
    },
    ProducingPL: {
      name: "Producing",
      lossId: "ProducingPL",
      subLossGroups: [
        "SpeedLossPL",
        "SpeedGainPL",
        "QualityLossPL",
        "RunningPL",
      ],
    },
    "Slow Cycles": {
      name: "Slow Cycles",
      lossId: "Slow Cycles",
      subLossGroups: [],
    },
    SpeedGainPL: {
      name: "Speed Gain",
      lossId: "SpeedGainPL",
      subLossGroups: [],
    },
    SpeedLossPL: {
      name: "Speed Loss",
      lossId: "SpeedLossPL",
      subLossGroups: [],
    },
    Uncollected: {
      name: "Uncollected",
      lossId: "Uncollected",
      subLossGroups: [],
    },
    UnplannedPL: {
      name: "Unplanned",
      lossId: "UnplannedPL",
      subLossGroups: ["Idle/Other", "Idle/Unplanned"],
    },
    "Idle/Planned": {
      date: "2021-06-28",
      name: "Planned",
      lossId: "Idle/Planned",
      suppressProducingInStickyTag: false,
    },
    "Idle/Unknown": {
      date: "2019-07-02",
      name: "Unknown",
      lossId: "Idle/Unknown",
    },
    "Idle/Waiting": {
      date: "2021-06-28",
      name: "Waiting",
      lossId: "Idle/Waiting",
      suppressProducingInStickyTag: false,
    },
    "Idle/Untagged": {
      date: "2019-07-02",
      name: "Untagged",
      lossId: "Idle/Untagged",
    },
    QualityLossPL: {
      name: "Quality Loss",
      lossId: "QualityLossPL",
      subLossGroups: [],
    },
    UncollectedPL: {
      name: "Uncollected",
      lossId: "UncollectedPL",
      subLossGroups: ["Uncollected"],
    },
    "Idle/Unplanned": {
      date: "2021-06-28",
      name: "Unplanned",
      lossId: "Idle/Unplanned",
      suppressProducingInStickyTag: false,
    },
    OperationLossPL: {
      name: "Operation Loss",
      lossId: "OperationLossPL",
      subLossGroups: [
        "Idle/NoProductionScheduled",
        "NoProductionScheduled(Estimated)",
      ],
    },
    "Planned Downtime": {
      name: "Planned Downtime",
      lossId: "Planned Downtime",
      subLossGroups: [],
    },
    ProductionLossPL: {
      name: "Production Loss",
      lossId: "ProductionLossPL",
      subLossGroups: ["UnknownPL", "UnplannedPL", "WaitingPL", "PlannedPL"],
    },
    "Setup and Adjust": {
      name: "Setup and Adjust",
      lossId: "Setup and Adjust",
      subLossGroups: [],
    },
    "Unscheduled Time": {
      name: "Unscheduled Time",
      lossId: "Unscheduled Time",
      subLossGroups: [],
    },
    "Equipment Downtime": {
      name: "Equipment Downtime",
      lossId: "Equipment Downtime",
      subLossGroups: [],
    },
    "Planned Maintenance": {
      name: "Planned Maintenance",
      lossId: "Planned Maintenance",
      subLossGroups: [],
    },
    "Unaccounted for Time": {
      name: "Unaccounted for Time",
      lossId: "Unaccounted for Time",
      subLossGroups: [],
    },
    NoProductionScheduledPL: {
      name: "No Production Scheduled",
      lossId: "NoProductionScheduledPL",
      subLossGroups: ["PlantLossPL", "OperationLossPL"],
    },
    "Idle/NoProductionScheduled": {
      date: "2021-06-28",
      name: "No Production Scheduled",
      lossId: "Idle/NoProductionScheduled",
      suppressProducingInStickyTag: false,
    },
    "Material and Labour Shortage": {
      name: "Material and Labour Shortage",
      lossId: "Material and Labour Shortage",
      subLossGroups: [],
    },
    "NoProductionScheduled(Estimated)": {
      date: "2018-12-12",
      name: "No Production Scheduled (Estimated)",
      lossId: "NoProductionScheduled(Estimated)",
    },
  },
  quickLinks: ["last_7_days", "last_30_days"],
  thresholds: {
    OEE: 0.9,
    TEEP: 0.9,
    Planned: 0.9,
    Quality: 0.9,
    Tagging: 0.95,
    Scheduled: 0.9,
    Performance: 0.9,
    Availability: 0.9,
    RavenCoverage: 0.99851,
    "Effective Time": 0.9,
  },
  hasAutoTags: false,
  shiftGroups: {
    "1": {
      end: "14:00",
      days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      name: "Day",
      start: "06:00",
      shiftGroupId: "1",
    },
    "2": {
      end: "22:00",
      days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      name: "Evening",
      start: "14:00",
      shiftGroupId: "2",
    },
    "3": {
      end: "06:00",
      days: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
      name: "Night",
      start: "22:00",
      shiftGroupId: "3",
    },
    "All Shifts": {
      name: "All Shifts",
      shiftGroupId: "All Shifts",
      subShiftGroups: ["1", "2", "3"],
    },
  },
  useMidgard2: true,
  customerLogo: "ravenjest2.jpg",
  customerName: "RavenJest2",
  hasValueMaps: false,
  lossInversion: {
    ProducingPL: "Production Time",
    UncollectedPL: "Calendar Time",
    ProductionLossPL: "Scheduled Time",
    NoProductionScheduledPL: "Collected Time",
  },
  machineGroups: {
    MG4: {
      name: "MG4",
      machineGroupId: "MG4",
      subMachineGroups: ["ravenjest2_d_002", "ravenjest2_d_001"],
    },
    MG5: {
      name: "MG5",
      machineGroupId: "MG5",
      subMachineGroups: ["ravenjest2_d_004", "ravenjest2_d_003"],
    },
    MG6: {
      name: "MG6",
      machineGroupId: "MG6",
      subMachineGroups: ["ravenjest2_d_005", "ravenjest2_d_006"],
    },
    "All Machines": {
      name: "All Machines",
      machineGroupId: "All Machines",
      subMachineGroups: [
        "ravenjest2_d_001",
        "ravenjest2_d_002",
        "ravenjest2_d_003",
        "ravenjest2_d_004",
        "ravenjest2_d_005",
        "ravenjest2_d_006",
      ],
    },
    "Machine Group 4": {
      name: "Machine Group 4",
      machineGroupId: "Machine Group 4",
      subMachineGroups: ["ravenjest2_d_002", "ravenjest2_d_001"],
    },
    "Machine Group 5": {
      name: "Machine Group 5",
      machineGroupId: "Machine Group 5",
      subMachineGroups: ["ravenjest2_d_004", "ravenjest2_d_003"],
    },
    "Machine Group 6": {
      name: "Machine Group 6",
      machineGroupId: "Machine Group 6",
      subMachineGroups: ["ravenjest2_d_005", "ravenjest2_d_006"],
    },
    ravenjest2_d_001: {
      name: "Machine 1",
      machineGroupId: "ravenjest2_d_001",
    },
    ravenjest2_d_002: {
      name: "Machine 2",
      machineGroupId: "ravenjest2_d_002",
    },
    ravenjest2_d_003: {
      name: "Machine 3",
      machineGroupId: "ravenjest2_d_003",
    },
    ravenjest2_d_004: {
      name: "Machine 4",
      machineGroupId: "ravenjest2_d_004",
    },
    ravenjest2_d_005: {
      name: "Machine 5",
      machineGroupId: "ravenjest2_d_005",
    },
    ravenjest2_d_006: {
      name: "Machine 6",
      machineGroupId: "ravenjest2_d_006",
    },
  },
  scheduleStart: "2018-01-01",
  useMimirCloud: true,
  groupThreshold: 60,
  operatorGroups: {
    "All Operators": {
      name: "All Operators",
      operatorGroupId: "All Operators",
      subOperatorGroups: [],
    },
  },
  productionUnits: "Units",
  operatorSchedule: [],
  customerNavBarLogo: "ravenjest2.png",
  clockDriftThreshold: 1,
  rateUnitsScaleFactor: 1,
  lossesToExcludeFromOee: "NoProductionScheduledPL",
  hasDataSourcesConfigFile: true,
  historicalOperatorGroups: {
    Default: {
      name: "Default",
      operatorGroupId: "Default",
      subOperatorGroups: ["All Operators"],
    },
  },
  useSkuTargetWithoutScaling: false,
};
